import { RowSelectedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { H2 } from '../../../../common/Headings/Headings.styles';
import { TableRowWrapper } from '../../../../common/SideBySideTableLayout/SideBySideTableLayout.styles';
import Button from '../../../../components/Button/Button';
import { Variant } from '../../../../components/Button/Button.types';
import Table from '../../../../components/Table/Table';
import TreeView from '../../../../components/TreeView/TreeView';
import { useGetClasses } from '../../../../services/class/class.service';
import { useGetRecipe } from '../../../../services/recipe/recipe.service';
import { Buttons, SelectRecipesContainer, StyledTableWrapper } from './SelectRecipes.styles';
import { SelectRecipesProps } from './SelectRecipes.types';

const SelectRecipes: FC<SelectRecipesProps> = ({
  selectedRecipes,
  checkedRows,
  expandedRows,
  handleRowClick,
  handleCheckboxClick,
  dataStructure,
  setStep,
  setSelectedRecipes,
  flow = 'addRawProd',
}) => {
  const { data: classes } = useGetClasses();
  const gridRef = useRef<AgGridReact>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchErrorMessage, setSearchErrorMessage] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const { data: searchedRecipe, isError, remove } = useGetRecipe({recipeId: +searchInputValue}, searchActive);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (searchActive) {
      setSearchActive(false);
      remove();
    }
    setSearchInputValue(e.target.value);
    setSearchErrorMessage('');
  };

  const handleSearchButtonClick = () => {
    setSearchActive(true);
  };

  const handleClear = () => {
    setSearchActive(false);
    setSearchErrorMessage('');
    setSearchInputValue('');
    remove();
  };

  useEffect(() => {
    if (searchedRecipe) {
      if (checkedRows.findIndex((checkedRow) => checkedRow.specNo === searchedRecipe.data.recipeId) === -1) {
        handleCheckboxClick({
          ...searchedRecipe.data,
          rowChecked: true,
          level: 4,
          parentRows: [
            { level: 0, id: searchedRecipe.data.classId },
            { level: 1, id: searchedRecipe.data.baseId },
            { level: 2, id: searchedRecipe.data.sizeId },
            { level: 3, id: searchedRecipe.data.productId },
            { level: 4, id: searchedRecipe.data.recipeId },
          ],
        });
      }
      setSearchInputValue('');
      setSearchActive(false);
      remove();
    }
  }, [searchedRecipe]);

  useEffect(() => {
    if (isError) {
      setSearchErrorMessage('Recipe not found');
    }
  }, [isError]);

  useEffect(() => {
    gridRef?.current?.api?.selectAll();
  }, [selectedRecipes, gridRef?.current?.api]);

  const handleRowSelected = (event: RowSelectedEvent) => {
    if (!event.node.isSelected()) {
      handleCheckboxClick({ ...event.data, rowChecked: false });
      setSelectedRecipes((selectedRecipes) => {
        const newSelectedRows = [...selectedRecipes];
        const indexToRemove = newSelectedRows.findIndex((spec) => spec.specNo === event.data.specNo);
        if (indexToRemove !== -1) newSelectedRows.splice(indexToRemove, 1);
        return newSelectedRows;
      });
    }
  };

  const handleBackClick = () => {
    if (flow === 'addRawProd' || flow === 'changeRawProds') setStep(0);
  };

  const handleNextClick = () => {
    if (flow === 'copy') setStep(1);
    if (flow === 'addRawProd') setStep(2);
    if (flow === 'changeRawProds') setStep(3);
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setSearchActive(true);
    }
  }
  useEffect(() => {
    if(inputRef.current) {
      inputRef.current.focus();
    }
  })

  return (
    <SelectRecipesContainer>
      {flow === 'copy' ? <></> : <H2>Select recipes to update</H2>}
      <TableRowWrapper>
        {classes && (
          <StyledTableWrapper>
            <TreeView
              rows={classes.data}
              dataStructure={dataStructure}
              checkedRows={checkedRows}
              expandedRows={expandedRows}
              onCheckboxClick={handleCheckboxClick}
              onRowClick={handleRowClick}
              expandOnCheck={false}
            />
          </StyledTableWrapper>
        )}
        <StyledTableWrapper>
          <Table
            rowSelection='multiple'
            suppressRowClickSelection={true}
            onRowSelected={handleRowSelected}
            ref={gridRef}
            showHeader={true}
            headerButtons={[
              {
                inputProps: {
                  label: '',
                  id: 'addSpec',
                  testId: 'AddSpecInput',
                  value: searchInputValue,
                  onChange: handleSearchInputChange,
                  errorText: searchErrorMessage,
                  'aria-label': 'add spec',
                  onKeyDown: handleKeyDown,
                  ref: inputRef,
                },
                buttonProps: {
                  label: searchErrorMessage ? 'Clear' : 'Add Spec',
                  testId: 'addSpecButton',
                  onClick: searchErrorMessage ? handleClear : handleSearchButtonClick,
                },
              },
            ]}
            headerTitle='Selected Recipes'
            columnDefs={[
              {
                width: 48,
                field: '',
                headerName: '',
                checkboxSelection: true,
                floatingFilter: false,
                sortable: false,
              },
              { minWidth: 220, field: 'specNo', headerName: 'Recipe', flex: 1 },
              { minWidth: 320, field: 'description', headerName: 'Description', flex: 1 },
            ]}
            returnedRecords={selectedRecipes.length}
            rowData={selectedRecipes}
            height={'100%'}
          />
        </StyledTableWrapper>
      </TableRowWrapper>
      <Buttons>
        {flow === 'copy' ? <div></div> : <Button label='Back' variant={Variant.Secondary} onClick={handleBackClick} />}
        <Button label='Next' testId='nextGoToReview' onClick={handleNextClick} disabled={selectedRecipes.length === 0} />
      </Buttons>
    </SelectRecipesContainer>
  );
};

export default SelectRecipes;
